/**
 * WebRTC, browser half. See `webrtc.ts` for why this file exists at all.
 *
 * Everything here is a global the browser already has, so this half is mostly a
 * re-export with the same names the native half uses. The only real difference is
 * `streamKey`: native renders a track from a URL string the library mints, the
 * browser renders it by assigning the MediaStream object to `video.srcObject`. Both
 * halves therefore hand the surface an opaque value and let the surface — which is
 * also split — know what to do with it.
 *
 * `WEBRTC_AVAILABLE` is a runtime check rather than a constant `true`: a browser old
 * enough to lack RTCPeerConnection, or a page served over plain http from a
 * non-localhost origin (where the API is unavailable by policy), must show "not
 * supported here" instead of throwing on the first click.
 */
export type IceServer = {
  urls: string | string[];
  username?: string;
  credential?: string;
};

/** Ровно то, чем пользуется пульт PTZ; у браузера и react-native-webrtc оно одинаковое. */
export type DataChannel = {
  label: string;
  readyState: string;
  send: (data: string) => void;
  onopen: (() => void) | null;
  onclose: (() => void) | null;
};

export type PeerConnection = {
  addTransceiver: (kind: string, init?: {direction?: string}) => unknown;
  setRemoteDescription: (desc: {type: string; sdp: string}) => Promise<void>;
  setLocalDescription: (desc?: unknown) => Promise<void>;
  createAnswer: () => Promise<{type: string; sdp: string}>;
  addIceCandidate: (candidate: unknown) => Promise<void>;
  getStats: () => Promise<Map<string, Record<string, unknown>> | unknown>;
  close: () => void;
  localDescription: {type: string; sdp: string} | null;
  onicecandidate: ((event: {candidate: unknown}) => void) | null;
  ontrack: ((event: {streams: unknown[]; track?: unknown}) => void) | null;
  oniceconnectionstatechange: (() => void) | null;
  /** Канал, открытый ДАЛЬНЕЙ стороной: регистратор кладёт в оффер `ptz` (см. ptz-channel). */
  ondatachannel?: ((event: {channel: DataChannel}) => void) | null;
  iceConnectionState: string;
  iceGatheringState?: string;
  connectionState?: string;
  signalingState?: string;
};

export type RemoteStream = MediaStream;

export const WEBRTC_AVAILABLE =
  typeof globalThis !== 'undefined' &&
  typeof (globalThis as {RTCPeerConnection?: unknown}).RTCPeerConnection === 'function';

export function createPeerConnection(
  iceServers: IceServer[],
  policy: 'all' | 'relay',
): PeerConnection {
  return new RTCPeerConnection({
    iceServers,
    iceTransportPolicy: policy,
  }) as unknown as PeerConnection;
}

export function sessionDescription(
  type: string,
  sdp: string,
): {type: string; sdp: string} {
  return {type, sdp};
}

export function iceCandidate(init: {
  candidate: string;
  sdpMid?: string | null;
  sdpMLineIndex?: number | null;
}): unknown {
  return new RTCIceCandidate(init as RTCIceCandidateInit);
}

/**
 * The browser has no per-stream URL to hand out — `URL.createObjectURL(stream)` was
 * removed years ago — so the surface gets the object and assigns `srcObject`. The id
 * is returned only so React has something stable to key on.
 */
export function streamKey(stream: RemoteStream): string {
  return stream.id;
}

/**
 * A stream for a track that arrived without one. A `track` event carries no stream
 * when the offer has no `a=msid` line — legal, and what some camera firmwares send —
 * and a player that waits for `streams[0]` then sits on "connecting" forever while
 * media is flowing. The browser can wrap the bare track itself.
 */
export function streamFromTrack(track: unknown): RemoteStream | null {
  return track ? new MediaStream([track as MediaStreamTrack]) : null;
}
